/**
 * Ошибка API в формате problem+json (ТЗ 16.3), разобранная в типизированный объект.
 * Сетевые сбои тоже приводятся к ApiProblem со статусом 0 и кодом `network`.
 */
export interface FieldProblem {
  field: string
  code: string
  message: string
}

export class ApiProblem extends Error {
  readonly status: number
  readonly code: string
  readonly title: string | null
  readonly errors: FieldProblem[]
  /** Дополнительные поля ответа: `current`, `existingBookId`, `maxBytes`. */
  readonly extra: Record<string, unknown>
  readonly retryAfter: number | null

  constructor(init: {
    status: number
    code: string
    title?: string | null
    detail?: string | null
    errors?: FieldProblem[]
    extra?: Record<string, unknown>
    retryAfter?: number | null
  }) {
    super(init.detail ?? init.title ?? init.code)
    this.name = 'ApiProblem'
    this.status = init.status
    this.code = init.code
    this.title = init.title ?? null
    this.errors = init.errors ?? []
    this.extra = init.extra ?? {}
    this.retryAfter = init.retryAfter ?? null
  }

  get isNetwork(): boolean {
    return this.status === 0
  }

  /** Сообщение для поля формы: путь `copy.price.amount` или `authors[0].name`. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {}
    for (const error of this.errors) {
      result[error.field] ??= error.message
    }
    return result
  }
}

const KNOWN = new Set([
  'type',
  'title',
  'status',
  'code',
  'detail',
  'requestId',
  'errors',
  'instance',
])

export function problemFromBody(status: number, body: unknown, headers?: Headers): ApiProblem {
  const data = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {}
  const extra: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(data)) {
    if (!KNOWN.has(key)) extra[key] = value
  }
  const retry = Number(headers?.get('Retry-After') ?? '')
  return new ApiProblem({
    status,
    code:
      typeof data.code === 'string' ? data.code : status >= 500 ? 'internal_error' : 'bad_request',
    title: typeof data.title === 'string' ? data.title : null,
    detail: typeof data.detail === 'string' ? data.detail : null,
    errors: Array.isArray(data.errors) ? (data.errors as FieldProblem[]) : [],
    extra,
    retryAfter: Number.isFinite(retry) && retry > 0 ? retry : null,
  })
}

export function networkProblem(): ApiProblem {
  return new ApiProblem({ status: 0, code: 'network', detail: 'Нет связи с сервером' })
}

export function isProblem(error: unknown, code?: string): error is ApiProblem {
  return error instanceof ApiProblem && (code === undefined || error.code === code)
}
